import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '@/services/auth';
import { toast } from 'react-hot-toast';
import { User, LogOut, ChevronDown, Search, Filter, Calendar, Paperclip, Mail, X, ChevronRight, Palette, UserPlus, ArrowRightLeft, Trash2 } from 'lucide-react';
import { useAccountStore } from '@/store/accountStore';
import { useDebounce } from '@/hooks/useDebounce';
import { searchService, EmailSearchParams, SearchResponse, SearchMessages } from '@/services/search';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useThemeStore } from '@/store/themeStore';
import { APP_THEMES, isDefaultTheme, isThemeSelected } from '@/config/themes';
import { Modal, ModalContent, ModalTitle } from '@/components/ui/Modal';
import { Menu } from 'lucide-react';
import { useSidebar } from '@/contexts/SidebarContext';
import './TopNav.css';

const TopNav: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchFilters, setShowSearchFilters] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchMessages[]>([]);
  const [searchPagination, setSearchPagination] = useState<SearchResponse['pagination'] | null>(null);
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const profileRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const currentUser = authService.getCurrentUser();
  const [isThemeModalOpen, setIsThemeModalOpen] = useState(false);
  const [isAllAccountsModalOpen, setIsAllAccountsModalOpen] = useState(false);
  const { currentTheme, setCurrentTheme, setPreviewTheme } = useThemeStore();
  const { setSidebarOpen } = useSidebar();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const { accounts, activeAccountId, setActiveAccount } = useAccountStore();

  // Check authentication on mount and when currentUser changes
  useEffect(() => {
    if (!authService.isAuthenticated() || !currentUser) {
      navigate('/login', { replace: true });
    }
  }, [navigate, currentUser]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Check initial size

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [filters, setFilters] = useState<Omit<EmailSearchParams, 'query'>>({
    searchIn: 'all' as 'all' | 'subject' | 'body' | 'from' | 'to',
    limit: 20,
    page: 1,
    since: undefined,
    before: undefined,
    hasAttachments: false,
    unread: false
  });

  const [expandedFilters, setExpandedFilters] = useState({
    dateRange: false,
    additionalFilters: false
  });

  // Helper function to toggle filter sections
  const toggleFilterSection = (section: keyof typeof expandedFilters) => {
    setExpandedFilters(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Date range presets
  const dateRangePresets = [
    { label: 'Today', getValue: () => {
      const today = new Date();
      return { start: today, end: today };
    }},
    { label: 'Last 7 Days', getValue: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 7);
      return { start, end };
    }},
    { label: 'Last 30 Days', getValue: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 30);
      return { start, end };
    }},
    { label: 'This Month', getValue: () => {
      const start = new Date();
      start.setDate(1);
      const end = new Date();
      return { start, end };
    }}
  ];

  const handleDateRangeChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setFilters(prev => ({
      ...prev,
      since: start ? start.toISOString() : undefined,
      before: end ? end.toISOString() : undefined
    }));
  };

  const handleDatePresetClick = (preset: typeof dateRangePresets[0]) => {
    const { start, end } = preset.getValue();
    setFilters(prev => ({
      ...prev,
      since: start.toISOString(),
      before: end.toISOString()
    }));
  };

  const clearDateRange = () => {
    setFilters(prev => ({
      ...prev,
      since: undefined,
      before: undefined
    }));
  };

  // Perform search
  const performSearch = async (query: string, currentFilters: EmailSearchParams) => {
    if (!query.trim()) {
      setSearchResults([]);
      setSearchPagination(null);
      return;
    }

    try {
      setLoading(true);
      const response = await searchService.searchEmails({
        ...currentFilters,
        query: query
      });
      setSearchResults(response.messages);
      setSearchPagination(response.pagination);
    } catch (error) {
      // Don't show error for cancelled requests
      if (error instanceof Error && error.message === 'Search request cancelled') {
        return;
      }
      console.error('Search error:', error);
      setSearchResults([]);
      setSearchPagination(null);
      toast.error('Failed to search emails');
    } finally {
      setLoading(false);
    }
  };

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Clear results if query is empty
    if (!query.trim()) {
      setSearchResults([]);
      setSearchPagination(null);
    }
  };

  // Handle filter changes
  const handleFilterChange = async (field: keyof EmailSearchParams, value: any) => {
    const newFilters = { 
      ...filters, 
      [field]: value,
      page: 1 // Reset to first page when changing filters
    };
    setFilters(newFilters);
    
    // Perform search with new filters if there's a query
    if (debouncedSearchQuery.trim()) {
      try {
        setLoading(true);
        const response = await searchService.searchEmails({
          ...newFilters,
          query: debouncedSearchQuery
        });
        setSearchResults(response.messages);
        setSearchPagination(response.pagination);
      } catch (error: any) {
        if (error.message !== 'Search request cancelled') {
          console.error('Search error:', error);
          toast.error('Failed to search emails');
          setSearchResults([]);
          setSearchPagination(null);
        }
      } finally {
        setLoading(false);
      }
    }
  };

  // Perform search when debounced query changes
  useEffect(() => {
    if (debouncedSearchQuery) {
      performSearch(debouncedSearchQuery, { ...filters, query: debouncedSearchQuery });
    }
  }, [debouncedSearchQuery, filters]);

  // Automatically collapse filters when results are loaded
  useEffect(() => {
    if (searchResults.length > 0) {
      setExpandedFilters({
        dateRange: false,
        additionalFilters: false
      });
    }
  }, [searchResults]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSearchFilters(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = async (accountId?: string) => {
    try {
      await authService.logout(accountId);
      if (!accountId || accounts.length <= 1) {
        // If logging out all accounts or last account
        toast.success('Logged out successfully');
        navigate('/login', { replace: true });
      } else {
        // If logging out a specific account but others remain
        toast.success('Account removed successfully');
        setIsProfileOpen(false);
      }
    } catch (error) {
      toast.error('Failed to logout');
    }
  };

  const handleAddAccount = () => {
    setIsProfileOpen(false);
    navigate('/login', { 
      state: { 
        addAccount: true,
        returnTo: location.pathname 
      }
    });
  };

  const handleSwitchAccount = async (accountId: string) => {
    const account = accounts.find(acc => acc.id === accountId);
    setActiveAccount(accountId);
    setIsProfileOpen(false);
    
    // Show toast notification
    if (account) {
      toast.success(`Account switched to ${account.first_name} ${account.last_name}`, {
        style: {
          background: '#1f2937',
          color: '#fff',
          borderRadius: '8px',
          fontSize: '14px'
        },
        iconTheme: {
          primary: '#10b981',
          secondary: '#fff'
        },
        duration: 2000 // Show for 2 seconds
      });

      // Wait for a short delay to ensure toast is visible
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    // Force navigation to inbox with new account
    window.location.href = '/inbox';
  };

  const handleThemeSelect = (theme: (typeof APP_THEMES)[number]) => {
    setCurrentTheme(isDefaultTheme(theme) ? null : theme.path);
    setIsThemeModalOpen(false);
    setPreviewTheme(null);
  };

  const handleThemePreview = (theme: (typeof APP_THEMES)[number]) => {
    setPreviewTheme(isDefaultTheme(theme) ? null : theme.path);
  };

  const handleMouseLeave = () => {
    setPreviewTheme(null);
  };

  return (
    <header className="app-topbar">
      <div className="app-topbar__left">
        {isMobile && (
          <button
            type="button"
            className="app-topbar__menu-btn"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open menu"
          >
            <Menu size={22} />
          </button>
        )}

        <div
          ref={searchRef}
          className="search-container"
          style={{
            position: 'relative',
            width: isMobile ? '100%' : '500px',
            flex: isMobile ? '1' : 'none',
            minWidth: 0,
          }}
        >
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <Search size={18} style={{ position: 'absolute', left: '12px', color: '#6b7280' }} />
            <input
              type="text"
              className="search-input"
              placeholder="Search emails..."
              value={searchQuery}
              onChange={handleSearchChange}
              autoComplete="off"
              style={{
                width: '100%',
                padding: '8px 12px 8px 40px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                fontSize: '14px',
                outline: 'none',
                transition: 'border-color 0.2s ease',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#6366f1';
                setShowSearchFilters(true);
              }}
            />
            <button
              type="button"
              onClick={() => setShowSearchFilters(!showSearchFilters)}
              aria-label="Search filters"
              style={{
                position: 'absolute',
                right: '12px',
                background: 'transparent',
                border: 'none',
                padding: '4px',
                cursor: 'pointer',
                color: '#6b7280',
                borderRadius: '4px',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <Filter size={16} />
            </button>
          </div>

        {showSearchFilters && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              marginTop: '8px',
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              zIndex: 1000,
              maxHeight: '80vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
            }}
          >
            {/* Filters Section */}
            <div style={{ padding: '16px', borderBottom: '1px solid #e5e7eb' }}>
              <div style={{ marginBottom: '16px' }}>
                <label className="block text-sm font-medium text-gray-700 mb-2" style={{ marginBottom: '10px' }}>Search In</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                  {(['all', 'subject', 'body', 'from', 'to'] as const).map((option) => (
                    <button
                      key={option}
                      onClick={() => {
                        handleFilterChange('searchIn', option);
                        // Reset search results when changing search type
                        setSearchResults([]);
                        setSearchPagination(null);
                        // Trigger new search if there's a query
                        if (debouncedSearchQuery) {
                          const searchParams = {
                            ...filters,
                            searchIn: option,
                            query: debouncedSearchQuery
                          };
                          searchService.searchEmails(searchParams).then(response => {
                            setSearchResults(response.messages);
                            setSearchPagination(response.pagination);
                          }).catch(error => {
                            console.error('Search error:', error);
                            toast.error('Failed to search emails');
                          });
                        }
                      }}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb',
                        background: filters.searchIn === option ? '#f3f4f6' : 'white',
                        fontSize: '14px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        color: filters.searchIn === option ? '#8d00e4' : '#374151',
                        borderColor: filters.searchIn === option ? '#8d00e4' : '#e5e7eb',
                        fontWeight: filters.searchIn === option ? '500' : 'normal'
                      }}
                      onMouseEnter={(e) => {
                        if (filters.searchIn !== option) {
                          e.currentTarget.style.backgroundColor = '#f9fafb';
                          e.currentTarget.style.borderColor = '#d1d5db';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (filters.searchIn !== option) {
                          e.currentTarget.style.backgroundColor = 'white';
                          e.currentTarget.style.borderColor = '#e5e7eb';
                        }
                      }}
                    >
                      {option.charAt(0).toUpperCase() + option.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Date Range Section */}
              <div>
                <button
                  onClick={() => toggleFilterSection('dateRange')}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 0',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#374151',
                    marginBottom: expandedFilters.dateRange ? '12px' : '0'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Calendar size={16} />
                    <span className="text-sm font-medium">Date Range</span>
                    {(filters.since || filters.before) && (
                      <span style={{ 
                        fontSize: '12px', 
                        backgroundColor: '#e0e7ff', 
                        color: '#8d00e4',
                        padding: '2px 6px', 
                        borderRadius: '4px',
                        marginLeft: '8px'
                      }}>
                        Active
                      </span>
                    )}
                  </div>
                  <ChevronRight
                    size={16}
                    style={{
                      transform: expandedFilters.dateRange ? 'rotate(90deg)' : 'none',
                      transition: 'transform 0.2s ease'
                    }}
                  />
                </button>
                
                {expandedFilters.dateRange && (
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <DatePicker
                          selectsRange={true}
                          startDate={filters.since ? new Date(filters.since) : null}
                          endDate={filters.before ? new Date(filters.before) : null}
                          onChange={(dates: [Date | null, Date | null]) => handleDateRangeChange(dates)}
                          dateFormat="MMM d, yyyy"
                          placeholderText="Select date range"
                          isClearable={false}
                          className="date-picker-input"
                          customInput={
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              border: '1px solid #e5e7eb',
                              fontSize: '14px',
                              width: '100%',
                              cursor: 'pointer',
                              background: 'white'
                            }}>
                              <Calendar size={16} className="text-gray-500 mr-2" style={{ marginRight: '8px' }} />
                              <input
                                style={{
                                  border: 'none',
                                  outline: 'none',
                                  width: '100%',
                                  background: 'transparent'
                                }}
                                readOnly
                                value={
                                  filters.since && filters.before
                                    ? `${new Date(filters.since).toLocaleDateString()} - ${new Date(filters.before).toLocaleDateString()}`
                                    : 'Select date range'
                                }
                              />
                              {(filters.since || filters.before) && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    clearDateRange();
                                  }}
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    padding: '2px',
                                    cursor: 'pointer',
                                    color: '#6b7280'
                                  }}
                                >
                                  <X size={14} />
                                </button>
                              )}
                            </div>
                          }
                        />
                      </div>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {dateRangePresets.map((preset, index) => (
                          <button
                            key={index}
                            onClick={() => handleDatePresetClick(preset)}
                            style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: '1px solid #e5e7eb',
                              background: 'white',
                              fontSize: '12px',
                              color: '#6b7280',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f3f4f6';
                              e.currentTarget.style.borderColor = '#d1d5db';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'white';
                              e.currentTarget.style.borderColor = '#e5e7eb';
                            }}
                          >
                            {preset.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Additional Filters Section */}
              <div>
                <button
                  onClick={() => toggleFilterSection('additionalFilters')}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 0',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#374151',
                    marginBottom: expandedFilters.additionalFilters ? '12px' : '0'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Filter size={16} />
                    <span className="text-sm font-medium">Additional Filters</span>
                    {(filters.hasAttachments || filters.unread) && (
                      <span style={{ 
                        fontSize: '12px', 
                        backgroundColor: '#e0e7ff', 
                        color: '#8d00e4',
                        padding: '2px 6px', 
                        borderRadius: '4px',
                        marginLeft: '8px'
                      }}>
                        Active
                      </span>
                    )}
                  </div>
                  <ChevronRight
                    size={16}
                    style={{
                      transform: expandedFilters.additionalFilters ? 'rotate(90deg)' : 'none',
                      transition: 'transform 0.2s ease'
                    }}
                  />
                </button>

                {expandedFilters.additionalFilters && (
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', gap: '16px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={filters.hasAttachments || false}
                          onChange={(e) => handleFilterChange('hasAttachments', e.target.checked)}
                          style={{ cursor: 'pointer' }}
                        />
                        <Paperclip size={16} className="text-gray-500" />
                        Has Attachments
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={filters.unread || false}
                          onChange={(e) => handleFilterChange('unread', e.target.checked)}
                          style={{ cursor: 'pointer' }}
                        />
                        <Mail size={16} className="text-gray-500" />
                        Unread Only
                      </label>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Results Section */}
            <div style={{ flex: 1, overflowY: 'auto', maxHeight: '400px' }}>
              {loading ? (
                <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280' }}>
                  Loading...
                </div>
              ) : searchResults.length > 0 ? (
                <>
                  <div style={{ padding: '16px' }}>
                    {searchResults.map((message) => (
                      <div
                        key={message.uid}
                        onClick={() => {
                          setShowSearchFilters(false);
                          navigate(`/email/${message.folderPath}/${message.id}/${message.uid}`, {
                            state: { emailInitial: message }
                          });
                        }}
                        style={{
                          padding: '12px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          marginBottom: '8px',
                          border: '1px solid transparent'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f3f4f6';
                          e.currentTarget.style.borderColor = '#e5e7eb';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.borderColor = 'transparent';
                        }}
                      >
                        <span style={{ fontSize: '12px', color: '#6b7280' }}>From: {message.headers.from}</span>

                        <div style={{ fontSize: '14px', marginBottom: '4px', color: '#111827' }}>
                          {message.headers.subject}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280', display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '12px', color: '#6b7280' }}>To: {message.headers.to}</span>
                          <span>
                            {new Date(message.date).toLocaleDateString()} <br />
                            {/* <span style={{ fontSize: '12px', backgroundColor: '#f3f4f6', padding: '2px 4px', borderRadius: '4px', marginTop: '4px' }}>
                              {message.folderPath}
                            </span> */}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                  {searchPagination && (
                    <div style={{ 
                      padding: '16px', 
                      borderTop: '1px solid #e5e7eb',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: '14px',
                      color: '#6b7280'
                    }}>
                      <span>
                        Showing {((searchPagination.page - 1) * searchPagination.limit) + 1} - {Math.min(searchPagination.page * searchPagination.limit, searchPagination.total)} of {searchPagination.total}
                      </span>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => handleFilterChange('page', searchPagination.page - 1)}
                          disabled={!searchPagination.hasPrev}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            border: '1px solid #e5e7eb',
                            background: searchPagination.hasPrev ? 'white' : '#f3f4f6',
                            cursor: searchPagination.hasPrev ? 'pointer' : 'not-allowed',
                            opacity: searchPagination.hasPrev ? 1 : 0.5
                          }}
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => handleFilterChange('page', searchPagination.page + 1)}
                          disabled={!searchPagination.hasNext}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            border: '1px solid #e5e7eb',
                            background: searchPagination.hasNext ? 'white' : '#f3f4f6',
                            cursor: searchPagination.hasNext ? 'pointer' : 'not-allowed',
                            opacity: searchPagination.hasNext ? 1 : 0.5
                          }}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : debouncedSearchQuery ? (
                <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280' }}>
                  No results found
                </div>
              ) : null}
            </div>
          </div>
        )}
        </div>
      </div>

      <div className="app-topbar__right">
        {/* <button className="notification-btn">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
          </svg>
        </button> */}
        
        {/* Profile Dropdown */}
        <div ref={profileRef} className="app-topbar__profile-menu">
          <button
            type="button"
            className="app-topbar__profile-btn"
            onClick={() => setIsProfileOpen(!isProfileOpen)}
            aria-label="Account menu"
          >
            {currentUser?.first_name && currentUser?.last_name ? (
              <img
                src={`https://placehold.co/32x32/6366f1/white?text=${currentUser?.first_name[0].toUpperCase()}${currentUser?.last_name[0].toUpperCase()}`}
                alt="User"
                className="app-topbar__avatar"
              />
            ) : (
              <img
                src={`https://placehold.co/32x32/6366f1/white?text=${currentUser?.username[0].toUpperCase()}`}
                alt="User"
                className="app-topbar__avatar"
              />
            )}
            <ChevronDown
              size={16}
              className={`app-topbar__chevron${isProfileOpen ? ' app-topbar__chevron--open' : ''}`}
            />
          </button>

          {isProfileOpen && (
            <div className="app-topbar__dropdown">
              {/* User Info */}
              <div style={{ padding: '16px', borderBottom: '1px solid #f3f4f6' }}>
                {currentUser?.first_name && currentUser?.last_name ? (
                  <div style={{ fontWeight: '600', color: '#111827', marginBottom: '4px' }}>
                    {`${currentUser?.first_name} ${currentUser?.last_name}`.trim() || currentUser?.username || 'User'}
                  </div>
                ) : (
                  <div style={{ fontWeight: '600', color: '#111827', marginBottom: '4px' }}>
                    {currentUser?.username || 'User'}
                  </div>
                )}
                
                <div style={{ fontSize: '14px', color: '#6b7280' }}>
                  {currentUser?.username}
                </div>
                {Array.isArray(currentUser?.domains) && currentUser.domains.length > 0 && (
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                    {currentUser.domains[0]}{currentUser.domains.length > 1 ? ` +${currentUser.domains.length - 1} more` : ''}
                  </div>
                )}
              </div>
              
              {/* Menu Items */}
              <div style={{ padding: '8px 0' }}>
                {/* My Account Button */}
                <button
                  onClick={() => {
                    setIsProfileOpen(false);
                    navigate('/profile');
                  }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    background: 'transparent',
                    border: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease',
                    color: '#374151'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <User size={16} />
                  <span>My Account</span>
                </button>

                {/* Change Theme Button */}
                <button
                  onClick={() => {
                    setIsProfileOpen(false);
                    setIsThemeModalOpen(true);
                  }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    background: 'transparent',
                    border: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease',
                    color: '#374151'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <Palette size={16} />
                  <span>Change theme</span>
                </button>

                {/* Manage Accounts Section */}
                <div style={{ borderTop: '1px solid #f3f4f6', margin: '8px 0', paddingTop: '8px' }}>
                  <div style={{ padding: '8px 16px', fontSize: '12px', color: '#6b7280', fontWeight: 500 }}>
                    Manage Accounts
                  </div>
                  
                  {/* List first 3 accounts */}
                  <div style={{
                    maxHeight: isMobile ? 'calc(60vh - 200px)' : '400px',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    margin: '0 -8px',  // Compensate for parent padding
                    padding: '0 8px'    // Add back padding for content
                  }}>
                    {accounts.slice(0, 3).map(account => (
                      <button
                        key={account.id}
                        onClick={() => account.id !== activeAccountId && handleSwitchAccount(account.id)}
                        style={{ 
                          width: '100%',
                          padding: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          background: account.id === activeAccountId ? '#f8fafc' : 'transparent',
                          borderRadius: '8px',
                          position: 'relative',
                          transition: 'all 0.2s ease',
                          border: 'none',
                          cursor: account.id === activeAccountId ? 'default' : 'pointer',
                          textAlign: 'left'
                        }}
                        onMouseEnter={(e) => {
                          if (account.id !== activeAccountId) {
                            e.currentTarget.style.backgroundColor = '#f3f4f6';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (account.id !== activeAccountId) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        <div style={{
                          position: 'relative',
                          width: '40px',
                          height: '40px',
                        }}>
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            background: account.id === activeAccountId ? '#6366f1' : '#e2e8f0',
                            color: account.id === activeAccountId ? 'white' : '#64748b',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '16px',
                            fontWeight: 500,
                            transition: 'all 0.2s ease'
                          }}>
                            {account.first_name?.[0]}{account.last_name?.[0] || ''}
                          </div>
                          {account.id === activeAccountId && (
                            <div style={{
                              position: 'absolute',
                              bottom: '-1px',
                              right: '-1px',
                              width: '14px',
                              height: '14px',
                              borderRadius: '50%',
                              background: '#22c55e',
                              border: '2px solid white',
                            }} />
                          )}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ 
                            fontSize: '14px', 
                            color: '#111827',
                            fontWeight: account.id === activeAccountId ? '500' : '400',
                            marginBottom: '2px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {account.first_name} {account.last_name}
                          </div>
                          <div style={{ 
                            fontSize: '12px', 
                            color: '#64748b',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {account.username}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>

                  {/* Show all accounts button if there are more than 3 */}
                  {accounts.length > 3 && (
                    <button
                      onClick={() => setIsAllAccountsModalOpen(true)}
                      style={{
                        width: '100%',
                        padding: '8px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        background: 'transparent',
                        border: 'none',
                        color: '#8d00e4',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '500',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f5f3ff';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <ChevronDown size={16} />
                      Show all accounts ({accounts.length})
                    </button>
                  )}

                  {/* Account Management Buttons */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <button
                      onClick={handleAddAccount}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px 16px',
                        background: 'transparent',
                        border: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s ease',
                        color: '#374151'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <UserPlus size={16} />
                      <span>Add another account</span>
                    </button>

                    <button
                      onClick={() => setIsAllAccountsModalOpen(true)}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px 16px',
                        background: 'transparent',
                        border: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s ease',
                        color: '#dc2626'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <Trash2 size={16} />
                      <span>Remove account</span>
                    </button>
                  </div>
                </div>
                
                <div style={{ borderTop: '1px solid #f3f4f6', margin: '8px 0' }}></div>
                
                {/* Sign out button */}
                <button
                  onClick={() => {
                    setIsProfileOpen(false);
                    handleLogout();
                  }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    background: 'transparent',
                    border: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease',
                    color: '#dc2626'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <LogOut size={16} />
                  <span>Sign out of all accounts</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* All Accounts Modal */}
      <Modal open={isAllAccountsModalOpen} onOpenChange={setIsAllAccountsModalOpen}>
        <ModalContent>
          <ModalTitle>All Accounts</ModalTitle>
          <div style={{ 
            padding: '16px',
            maxHeight: isMobile ? 'calc(100vh - 200px)' : '600px',
            overflowY: 'auto'
          }}>
            {accounts.map(account => (
              <div
                key={account.id}
                style={{ 
                  padding: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  background: account.id === activeAccountId ? '#f8fafc' : 'transparent',
                  borderRadius: '8px',
                  position: 'relative',
                  transition: 'all 0.2s ease',
                  marginBottom: '8px'
                }}
              >
                <div style={{
                  position: 'relative',
                  width: '40px',
                  height: '40px',
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: account.id === activeAccountId ? '#6366f1' : '#e2e8f0',
                    color: account.id === activeAccountId ? 'white' : '#64748b',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    fontWeight: 500,
                    transition: 'all 0.2s ease'
                  }}>
                    {account.first_name?.[0]}{account.last_name?.[0] || ''}
                  </div>
                  {account.id === activeAccountId && (
                    <div style={{
                      position: 'absolute',
                      bottom: '-1px',
                      right: '-1px',
                      width: '14px',
                      height: '14px',
                      borderRadius: '50%',
                      background: '#22c55e',
                      border: '2px solid white',
                    }} />
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ 
                    fontSize: '14px', 
                    color: '#111827',
                    fontWeight: account.id === activeAccountId ? '500' : '400',
                    marginBottom: '2px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {account.first_name} {account.last_name}
                  </div>
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#64748b',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {account.username}
                  </div>
                </div>
                
                <div style={{ 
                  display: 'flex',
                  gap: '6px',
                  opacity: account.id === activeAccountId ? 0 : 1,
                  transition: 'opacity 0.2s ease'
                }}>
                  <button
                    onClick={() => {
                      handleSwitchAccount(account.id);
                      setIsAllAccountsModalOpen(false);
                    }}
                    style={{
                      padding: '6px',
                      borderRadius: '6px',
                      border: '1px solid #e2e8f0',
                      background: '#f8fafc',
                      color: '#0f172a',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#f1f5f9';
                      e.currentTarget.style.borderColor = '#cbd5e1';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#f8fafc';
                      e.currentTarget.style.borderColor = '#e2e8f0';
                    }}
                    title="Switch account"
                  >
                    <ArrowRightLeft size={16} />
                  </button>
                  <button
                    onClick={() => {
                      handleLogout(account.id);
                      setIsAllAccountsModalOpen(false);
                    }}
                    style={{
                      padding: '6px',
                      borderRadius: '6px',
                      border: '1px solid #fecaca',
                      background: '#fff1f2',
                      color: '#ef4444',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#fee2e2';
                      e.currentTarget.style.borderColor = '#fca5a5';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#fff1f2';
                      e.currentTarget.style.borderColor = '#fecaca';
                    }}
                    title="Remove account"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </ModalContent>
      </Modal>

      {/* Theme Modal */}
      <Modal open={isThemeModalOpen} onOpenChange={setIsThemeModalOpen}>
        <ModalContent>
          <ModalTitle>Select Theme</ModalTitle>
          <div className="grid grid-cols-3 gap-4 p-4">
            {APP_THEMES.map((theme) => (
              <div
                key={theme.id}
                className="relative cursor-pointer rounded-lg overflow-hidden"
                onMouseEnter={() => handleThemePreview(theme)}
                onMouseLeave={handleMouseLeave}
                onClick={() => handleThemeSelect(theme)}
              >
                {theme.thumbnail ? (
                  <img
                    src={theme.thumbnail}
                    alt={theme.name}
                    className="w-full h-24 object-cover"
                  />
                ) : (
                  <div className="w-full h-24 bg-gray-100 flex items-center justify-center">
                    Default
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white p-2 text-sm">
                  {theme.name}
                </div>
                {isThemeSelected(theme, currentTheme) && (
                  <div className="absolute top-2 right-2 bg-primary text-white text-xs px-2 py-1 rounded">
                    Current
                  </div>
                )}
              </div>
            ))}
          </div>
        </ModalContent>
      </Modal>
    </header>
  );
};

export default TopNav;